import { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useDebounce } from "use-debounce";

import ReactTable from "components/lib/Global/ReactTable";
import Sidebar from "components/Applications/Sidebar";
import HeroSection from "components/Applications/HeroSection";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import OrgAdminDashboardWrapper from "components/lib/Global/OrgAdminDashboardWrapper";
import SearchInput from "components/lib/Shared/SearchInput";
import Unauthorized from "pages/Unauthorized";
import SubRowAsync from "./SubRow";
import { getCompaniesAsync } from "state/slices/companies";
import Checkbox from "components/lib/Shared/Checkbox";
import GoBackToHomeBtn from "../GoBackToHomeBtn";
import { getCompanyNameWithSuffix } from "utils/company";
import { getTransactionListAsync } from "state/slices/transaction";
import { getMaintainYourCompanyReviewStatus } from "config/documentData";
import { format } from "date-fns";
import { Constants } from "config/constants";
import Cookies from "js-cookie";
import { getCompaniesTransactionsListAsync } from "state/slices/companiesTransactions";

const statuses = [
  { value: "Pending Incorporation", label: "Pending Incorporation" },
  { value: "BRS Review", label: "BRS Review" },
  { value: "BRS Review II", label: "BRS Review II" },
  { value: "BRS Corrections", label: "BRS Corrections" },
  { value: "Incorporation Deferred", label: "Incorporation Deferred" },
  { value: "Vetting", label: "Vetting" },
];

export default function BRSSubmission() {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useAuth0();
  const location = useLocation();
  const history = useHistory();

  const { getUserStatus } = useSelector((state) => state.authSlice);
  const { getCompaniesTransactionsList, companiesTransactionsList } =
    useSelector((state) => state.companiesTransactionsSlice);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState(() =>
    statuses.map((status) => status.value)
  );
  const { orgAdmin } = useSelector((state) => state.roleswitchSlice);

  const cookieRole = parseInt(Cookies.get("role"));
  const [debouncedSearchTerm] = useDebounce(searchTerm, 1000);
  useEffect(() => {
    if (isAuthenticated) {
      const queryParams = new URLSearchParams(location.search);
      if (debouncedSearchTerm) setCurrentPage(1);
      const status = queryParams.getAll("status");
      setFilters(status);

      let finalStatus = status.flatMap((s) => {
        if (s === "BRS Review") {
          return ["BRS Review", "BRS-REVIEW"];
        }
        return s;
      });

      finalStatus.push(
        Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_REVIEW,
        Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_REVIEW_II
      );

      dispatch(
        getCompaniesTransactionsListAsync({
          queryParams: {
            limit: rowsPerPage,
            skip: (currentPage - 1) * rowsPerPage,
            search: debouncedSearchTerm,
            status: finalStatus.length > 0 ? finalStatus : null,
            order: "asc",
            orgAdmin: orgAdmin ? orgAdmin : "",
          },
        })
      );
    }
  }, [
    isAuthenticated,
    rowsPerPage,
    currentPage,
    debouncedSearchTerm,
    location.search,
  ]);

  const currentUser = useSelector((state) => state.userSlice);

  const refreshTableData = async () => {
    dispatch(
      getCompaniesTransactionsListAsync({
        queryParams: {
          limit: rowsPerPage,
          skip: (currentPage - 1) * rowsPerPage,
          search: debouncedSearchTerm,
          status: [
            "Pending Incorporation",
            "BRS Review",
            "BRS-REVIEW", // Include BRS-REVIEW here
            "BRS Corrections",
            "Incorporation Deferred",
            "Vetting",
            Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_REVIEW,
            Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.BRS_REVIEW_II, // Include BRS-REVIEW here
          ],
          order: "asc",
          orgAdmin: orgAdmin ? orgAdmin : "",
        },
      })
    );
  };

  // this function will render our row sub components
  const renderRowSubComponent = useCallback(
    ({ row, rowProps, visibleColumns }) => {
      return (
        <SubRowAsync
          row={row}
          rowProps={rowProps}
          visibleColumns={visibleColumns}
          refreshTableData={refreshTableData}
        />
      );
    },
    []
  );

  if (!currentUser?._id || getUserStatus.status === "loading") {
    return (
      <OrgAdminDashboardWrapper>
        <div className="flex justify-center mx-auto mt-10 text-white">
          <SpinnerIcon className="text-gray-400" />
        </div>
      </OrgAdminDashboardWrapper>
    );
  }

  const columns = [
    {
      Header: "Batch No.",
      accessor: "BatchNo",
      Cell: ({ row }) => row.original?.batchNo || "-",
    },
    {
      Header: "Name.",
      accessor: "companyName1",
      Cell: ({ row }) => {
        if (row.original?.companyInfo) {
          return getCompanyNameWithSuffix(row.original.companyInfo);
        } else {
          return getCompanyNameWithSuffix(row.original);
        }
      },
    },
    {
      Header: "Application Type",
      accessor: "applicationType",

      Cell: ({ row }) => {
        if (row.original?.applicationType) {
          return row.original?.applicationType;
        } else {
          if (row.original?.transferYourBusinessInitiator) {
            return "Transfer Your Business";
          }
          return "Incorporation";
        }
      },
    },
    {
      Header: "Change Type",
      accessor: "changeType",
      Cell: ({ row: { original } }) => {
        const capitalize = (str) => {
          if (!str) return null;
          return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
        };

        return (
          capitalize(original?.changeType) || original?.companyInfo?.type || "-"
        );
      },
    },
    {
      Header: "Transaction Type",
      accessor: "transactionType",
      Cell: ({ row: { original } }) => {
        const capitalize = (str) => {
          if (!str) return null;
          let transactionType =
            str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
          return transactionType;
        };

        return capitalize(original?.transactionType) || "Automatic";
      },
    },
    {
      Header: "Tnx No.",
      accessor: "TnxNo",
      Cell: ({ row: { original } }) => {
        return (
          original?.trxRef || original?.companyInfo?.registrationNumber || "-"
        );
      },
    },
    {
      Header: "Date",
      Cell: ({ row: { original } }) => {
        const date = original?.createdAt;
        return date ? format(new Date(date), "yyyy/MM/dd") : "N/A";
      },
    },
    {
      Header: "Status",
      Cell: ({ row }) => {
        if (row.original?.applicationType) {
          return getMaintainYourCompanyReviewStatus(row.original?.status);
        } else {
          if (row.original.status.includes("II")) {
            return (
              <>
                {row.original.status}{" "}
                <span className="items-center gap-x-1.5 rounded-full px-2 py-1 text-xxs font-medium text-gray-900 ring-1 ring-inset ring-green-primary bg-green-100/25 tracking-wide">
                  Corrected
                </span>
              </>
            );
          }
          if (
            ["Admin Review", "Submission", "BRS Review"].includes(
              row.original.status
            )
          ) {
            return (
              <>
                {row.original.status}{" "}
                <span className="items-center gap-x-1.5 rounded-full px-2 py-1 text-xxs font-medium text-gray-900 ring-1 ring-inset ring-primary-200 bg-purple-100/25 tracking-wide">
                  New
                </span>
              </>
            );
          }
          return row.original.status;
        }
      },
    },
    {
      Header: "Action",
      Cell: ({ row }) => {
        return (
          <button
            type="button"
            {...row?.getToggleRowExpandedProps()}
            className={`text-gray-900 group flex rounded-md items-center w-full px-2 py-1 text-sm disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500`}
          >
            {row.isExpanded ? "Close" : "View"}
          </button>
        );
      },
    },
  ];

  function handleSearch(event) {
    setSearchTerm(event.target.value);
  }

  const handleFilterChange = (e) => {
    const params = new URLSearchParams();
    if (e.target.checked) {
      const tempFilters = [...filters, e.target.value];
      tempFilters.forEach((status) => params.append("status", status));
      history.push({ search: params.toString() });
      setFilters([...filters, e.target.value]);
    } else {
      const tempFilters = filters.filter((filter) => filter !== e.target.value);
      tempFilters.forEach((status) => params.append("status", status));
      history.push({ search: params.toString() });
      setFilters(tempFilters);
    }
    setCurrentPage(1);
  };

  if (
    (cookieRole === 9 || currentUser?.role === 9) &&
    getUserStatus.status !== "loading"
  ) {
    return (
      <OrgAdminDashboardWrapper>
        <div className=" grid gap-4 py-10 mx-auto">
          <HeroSection />
          <GoBackToHomeBtn />
          <div className="grid grid-cols-6 border-t border-gray">
            <div className="col-span-6 pr-8 md:col-span-1">
              <Sidebar />
            </div>
            <div className="col-span-6 md:border-l md:col-span-5 md:pl-8 pt-8 border-gray">
              <div className="mb-6">
                <h1 className="text-title5">BRS Submitted</h1>
              </div>
              <div className="flex items-center justify-between my-2">
                <div className="flex items-center space-x-4">
                  <h2 className="font-semibold">Filter by:</h2>
                  <div className="flex space-x-2 my-2">
                    {statuses.map((status) => (
                      <Checkbox
                        key={status.value}
                        name={status.label}
                        label={status.label}
                        checked={filters.includes(status.value)}
                        onChange={handleFilterChange}
                      />
                    ))}
                  </div>
                </div>
                <SearchInput value={searchTerm} handleOnChange={handleSearch} />
              </div>
              {companiesTransactionsList.items.length > 0 ? (
                <ReactTable
                  columns={columns}
                  data={companiesTransactionsList.items}
                  totalRows={companiesTransactionsList.totalCount}
                  currentPage={currentPage}
                  rowsPerPage={rowsPerPage}
                  setCurrentPage={setCurrentPage}
                  paginate
                  renderRowSubComponent={renderRowSubComponent}
                  isLoading={getCompaniesTransactionsList.status === "loading"}
                />
              ) : (
                <div className="flex items-center justify-center mt-10">
                  {getCompaniesTransactionsList.status === "loading" ? (
                    <SpinnerIcon className="text-gray-400" />
                  ) : (
                    <p className="text-2xl text-center text-primary">
                      No applications available.
                    </p>
                  )}
                </div>
              )}
              {!getCompaniesTransactionsList.status === "loading" && (
                <div className="flex items-center justify-center mt-10">
                  <p className="text-2xl text-center text-primary">
                    No applications available.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </OrgAdminDashboardWrapper>
    );
  } else {
    return <Unauthorized />;
  }
}
